import type { DraftRecord, Profile } from './types';

const DB_NAME = 'critical_control_hub';
const DB_VERSION = 1;

type StoreName = 'profile' | 'drafts' | 'history';

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('profile')) db.createObjectStore('profile', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('drafts')) db.createObjectStore('drafts', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('history')) db.createObjectStore('history', { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const txStore = async (storeName: StoreName, mode: IDBTransactionMode) => {
  const db = await openDb();
  const tx = db.transaction(storeName, mode);
  return { db, tx, store: tx.objectStore(storeName) };
};

export const dbGet = async <T>(storeName: StoreName, key: string): Promise<T | undefined> => {
  const { db, store } = await txStore(storeName, 'readonly');
  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => {
      db.close();
      resolve(request.result as T | undefined);
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
};

export const dbGetAll = async <T>(storeName: StoreName): Promise<T[]> => {
  const { db, store } = await txStore(storeName, 'readonly');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      db.close();
      resolve(request.result as T[]);
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
};

export const dbPut = async <T>(storeName: StoreName, value: T): Promise<void> => {
  const { db, tx, store } = await txStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    store.put(value);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
};

export const dbDelete = async (storeName: StoreName, key: string): Promise<void> => {
  const { db, tx, store } = await txStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    store.delete(key);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
};

export const loadProfile = () => dbGet<Profile>('profile', 'local_profile');
export const saveProfile = (profile: Profile) => dbPut('profile', profile);
export const loadDrafts = () => dbGetAll<DraftRecord>('drafts');
export const saveDraft = (draft: DraftRecord) => dbPut('drafts', draft);
export const deleteDraft = (id: string) => dbDelete('drafts', id);
export const loadHistory = () => dbGetAll<DraftRecord>('history');
export const saveHistory = (record: DraftRecord) => dbPut('history', record);
export const deleteHistory = (id: string) => dbDelete('history', id);
